import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './Products.css';
import { API_CONFIG } from '../../utils/apiConfig';
import { useAuth } from '../../contexts/AuthContext'; // Add this import

const ProductList = ({ userId }) => {
  const [products, setProducts] = useState([]);
  const [editIndex, setEditIndex] = useState(null);
  const [editedProduct, setEditedProduct] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saveMessage, setSaveMessage] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // 'asc' or 'desc'

  const navigate = useNavigate();
  const { userProfile } = useAuth(); // Get user profile

  // Check if user is super admin or purchase admin
  const canEditProducts =
    userProfile?.role === 'superadmin' ||
    userProfile?.role === 'super_admin' ||
    (userProfile?.role === 'admin' && userProfile?.department === 'purchase');

 const fetchProducts = useCallback(async () => {
  try {
    setLoading(true);
    
    const token = localStorage.getItem('token');
    if (!token) {
      setError('No authentication token found. Please log in.');
      setLoading(false);
      return;
    }
    
    const url = userId
      ? `${API_CONFIG.BASE_URL}/api/products/by-creator/${userId}`
      : `${API_CONFIG.BASE_URL}/api/products`;

    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    const fetchedProducts = res.data;
    const productsArray = Array.isArray(fetchedProducts) ? fetchedProducts : (fetchedProducts.data || fetchedProducts);
    const validProducts = Array.isArray(productsArray) 
      ? productsArray.filter(product => product.id && Object.keys(product).length > 1)
      : [];
    setProducts(validProducts);
    setLoading(false);
  } catch (err) {
    console.error('Fetch error:', err.response?.data || err);
    
    if (err.response?.status === 401) {
      setError('Authentication failed. Please log in again.');
    } else if (err.response?.status === 403) {
      setError('Access denied. You do not have permission to view products.');
    } else {
      setError('Error fetching products: ' + (err.response?.data?.error || err.message));
    }
    setLoading(false);
  }
}, [userId]);

useEffect(() => {
  fetchProducts();
}, [fetchProducts]);

  const handleEditClick = (index, event) => {
    // Prevent default behavior and stop event propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    const product = sortedProducts[index];
    if (!product || !product.id) {
      alert('Error: Cannot edit product with missing or invalid ID.');
      return;
    }
    setEditIndex(index);
    setEditedProduct({ ...product });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedProduct({
      ...editedProduct,
      [name]: ['gst', 'unitPrice', 'quantity'].includes(name) ? Number(value) || 0 : value,
    });
  };

  const handleSave = async () => {
    if (!editedProduct.id) {
      alert('Error: Product ID is missing.');
      console.error('Missing id in editedProduct:', editedProduct);
      return;
    }
    try {
      const token = localStorage.getItem('token');

      await axios.put(
        `${API_CONFIG.BASE_URL}/api/products/${editedProduct.id}`,
        editedProduct,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Update the product in the list with the edited data
      const updated = [...products];
      const updatedProduct = {
        ...editedProduct,
        itemCode: editedProduct.itemCode || '',
        itemName: editedProduct.itemName || '',
        hsn: editedProduct.hsn || '',
        gst: editedProduct.gst || 0,
        unitPrice: editedProduct.unitPrice || 0,
        quantity: editedProduct.quantity || 0
      };

      updated[editIndex] = updatedProduct;
      setProducts(updated);
      setEditIndex(null);
      setSaveMessage('Product updated successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Update error:', err.response?.data || err);
      alert('Error updating product: ' + (err.response?.data?.error || 'Unknown error'));
    }
  };

  const handleCancel = () => {
    setEditIndex(null);
  };
  const handleDelete = async (productId, productName, event) => {
    // Prevent default behavior and stop event propagation
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!productId) {
      alert('Error: Product ID is missing.');
      return;
    }

    const confirmDelete = window.confirm(
      `🗑️ DELETE PRODUCT\n\nAre you sure you want to delete:\n"${productName}"\n\n⚠️ This action cannot be undone!`
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setDeletingId(productId);
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API_CONFIG.BASE_URL}/api/products/${productId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Remove the product from the list
      const updatedProducts = products.filter(product => product.id !== productId);
      setProducts(updatedProducts);
      setSaveMessage('✅ Product deleted successfully');
      setTimeout(() => setSaveMessage(''), 3000);
    } catch (err) {
      console.error('Delete error:', err.response?.data || err);
      alert('❌ Error deleting product: ' + (err.response?.data?.error || 'Unknown error'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSortToggle = () => {
    setSortOrder(prevOrder => prevOrder === 'asc' ? 'desc' : 'asc');
  };

  // Sort products by item code
  const sortedProducts = [...products].sort((a, b) => {
    const itemCodeA = (a.itemCode || '').toLowerCase();
    const itemCodeB = (b.itemCode || '').toLowerCase();
    
    if (sortOrder === 'asc') {
      return itemCodeA.localeCompare(itemCodeB);
    } else {
      return itemCodeB.localeCompare(itemCodeA);
    }
  });

  // Calculate totals
  const totalProducts = products.length;
  const totalQuantity = products.reduce((sum, product) => {
    return sum + (product.quantity || 0);
  }, 0);

  if (loading) return <div>Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;

  return (
    <div className="product-list">
      <div className="product-list-header">
        <button onClick={handleBack} className="black-button"> Back</button>
        <h2>{userId ? 'Products Added by Purchase Admin' : 'All Products'}</h2>
        <div className="header-controls">
          <button 
            onClick={handleSortToggle} 
            className="sort-button"
            title={`Sort by Item Code (${sortOrder === 'asc' ? 'Ascending' : 'Descending'})`}
          >
            {sortOrder === 'asc' ? '↑ Sort A-Z' : '↓ Sort Z-A'}
          </button>
        </div>
        {saveMessage && <div className="success-message">{saveMessage}</div>}
      </div>
      
      {/* Summary Section */}
      <div className="product-summary">
        <div className="summary-card">
          <h3>📊 Inventory Summary</h3>
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Total Products:</span>
              <span className="stat-value">{totalProducts}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Total Quantity:</span>
              <span className="stat-value">{totalQuantity.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
      
      <table className="product-list-table">
        <thead>
          <tr>
            <th>Item Code</th>
            <th>Item Name</th>
            <th>HSN</th>
            <th>GST</th>
            <th>Price</th>
            <th>Qty</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedProducts.length === 0 ? (
            <tr>
              <td colSpan="7">No products found.</td>
            </tr>
          ) : (
            sortedProducts.map((product, idx) => (
              <tr key={`${product.id}-${idx}`} className="product-row">
                {editIndex === idx ? (
                  <>
                    <td><input name="itemCode" value={editedProduct.itemCode ?? ''} onChange={handleChange} /></td>
                    <td><input name="itemName" value={editedProduct.itemName ?? ''} onChange={handleChange} /></td>
                    <td><input name="hsn" value={editedProduct.hsn ?? ''} onChange={handleChange} /></td>
                    <td><input name="gst" value={editedProduct.gst ?? ''} type="number" onChange={handleChange} /></td>
                    <td><input name="unitPrice" value={editedProduct.unitPrice ?? ''} type="number" onChange={handleChange} /></td>
                    <td><input name="quantity" value={editedProduct.quantity ?? ''} type="number" onChange={handleChange} /></td>
                    <td>
                      {canEditProducts && (
                        <>
                          <button onClick={handleSave}>Save</button>
                          <button onClick={handleCancel}>Cancel</button>
                        </>
                      )}
                    </td>
                  </>
                ) : (
                  <>
                    <td>{product.itemCode || 'N/A'}</td>
                    <td>{product.itemName || 'N/A'}</td>
                    <td>{product.hsn || 'N/A'}</td>
                    <td>{product.gst != null ? product.gst : 'N/A'}</td>
                    <td>
                      {product.unitPrice != null
                        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(product.unitPrice)
                        : 'N/A'}
                    </td>
                    <td>{product.quantity != null ? product.quantity : 'N/A'}</td>
                    <td>
                      <div className="product-action-buttons">
                        {canEditProducts && (
                          <>
                            <button 
                              type="button"
                              onClick={(e) => handleEditClick(idx, e)}
                              className="edit-btn"
                              disabled={deletingId === product.id}
                            >
                              ✏️
                            </button>
                            <button 
                              type="button"
                              onClick={(e) => handleDelete(product.id, product.itemName, e)}
                              className="product-delete-btn"
                              disabled={deletingId === product.id}
                            >
                              {deletingId === product.id ? '⏳ Deleting...' : '🗑️'}
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default ProductList;